// filepath: src/component/page/poll/PollPage.jsx
import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    MenuItem,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {createPoll, deletePoll, fetchPolls, votePoll, hasUserVoted, getUserVote} from './redux/pollActions';
import {fetchClasses} from '../master/classsection/redux/Action';
import {fetchStaff} from '../staff/redux/staffActions';
import {selectSchoolDetails} from '../../../common';

/**
 * Poll object shape (frontend)
 * id: string
 * question: string
 * options: Array<{ id: string, text: string, votes: number }>
 * targetCategory: 'all' | 'class' | 'section' | 'teachers' | 'non-teaching'
 * classId?: string
 * sectionId?: string
 * createdAt: number
 */

const emptyOption = () => ({ id: Date.now().toString() + Math.random().toString(36).slice(2), text: '', votes: 0 });

const PollPage = () => {
    const dispatch = useDispatch();
    const { list: polls, loading } = useSelector(state => state.polls);
    const classesState = useSelector(state => state.classes?.classes || []);
    const staffList = useSelector(state => state.staff?.staffList || []);
    const schoolDetails = useSelector(selectSchoolDetails);
    const schoolId = schoolDetails?.id;
    const session = schoolDetails?.session;

    const [open, setOpen] = useState(false);
    const [question, setQuestion] = useState('');
    const [options, setOptions] = useState([emptyOption(), emptyOption()]);
    const [targetCategory, setTargetCategory] = useState('all');
    const [classId, setClassId] = useState('');
    const [sectionId, setSectionId] = useState('');
    const [error, setError] = useState('');
    const [votingPollId, setVotingPollId] = useState(null);
    const [selectedOption, setSelectedOption] = useState('');
    const [voteDialogOpen, setVoteDialogOpen] = useState(false);

    useEffect(() => { dispatch(fetchPolls()); }, [dispatch]);

    useEffect(() => {
        if (schoolId && session) {
            if (!classesState || classesState.length === 0) {
                dispatch(fetchClasses(schoolId, session));
            }
            if (!staffList || staffList.length === 0) {
                dispatch(fetchStaff(schoolId, session));
            }
        }
    }, [dispatch, schoolId, session]);

    const teachingStaff = useMemo(() => staffList.filter(s => (s.type || s.staffType || '').toLowerCase().includes('teach')), [staffList]);
    const nonTeachingStaff = useMemo(() => staffList.filter(s => !(s.type || s.staffType || '').toLowerCase().includes('teach')), [staffList]);

    // Handle opening the vote dialog
    const handleOpenVoteDialog = (pollId) => {
        setVotingPollId(pollId);
        setSelectedOption('');
        setVoteDialogOpen(true);
    };

    // Close vote dialog
    const handleCloseVoteDialog = () => {
        setVoteDialogOpen(false);
        setVotingPollId(null);
        setSelectedOption('');
    };

    // Handle vote submission
    const handleVoteSubmit = () => {
        if (!selectedOption || !votingPollId) return;
        dispatch(votePoll(votingPollId, selectedOption));
        handleCloseVoteDialog();
    };

    // Get current poll being voted on
    const currentPoll = useMemo(() => {
        return polls.find(p => p.id === votingPollId) || null;
    }, [polls, votingPollId]);

    // Calculate total votes for a poll
    const getTotalVotes = (poll) => {
        return poll.options.reduce((sum, option) => sum + (option.votes || 0), 0);
    };

    const resetForm = () => {
        setQuestion('');
        setOptions([emptyOption(), emptyOption()]);
        setTargetCategory('all');
        setClassId('');
        setSectionId('');
        setError('');
    };

    const handleAddOption = () => {
        setOptions(prev => [...prev, emptyOption()]);
    };

    const handleRemoveOption = (id) => {
        setOptions(prev => prev.length > 2 ? prev.filter(o => o.id !== id) : prev);
    };

    const handleOptionChange = (id, value) => {
        setOptions(prev => prev.map(o => o.id === id ? { ...o, text: value } : o));
    };

    const handleSubmit = () => {
        if (!question.trim()) { setError('Question is required'); return; }
        const filteredOptions = options.map(o => ({...o, text: o.text.trim()})).filter(o => o.text);
        if (filteredOptions.length < 2) { setError('At least two options required'); return; }
        if (targetCategory === 'class' && !classId) { setError('Select a class'); return; }
        if (targetCategory === 'section' && (!classId || !sectionId)) { setError('Select class & section'); return; }

        const payload = {
            id: Date.now().toString(),
            question: question.trim(),
            options: filteredOptions,
            targetCategory,
            classId: targetCategory === 'class' || targetCategory === 'section' ? classId : undefined,
            sectionId: targetCategory === 'section' ? sectionId : undefined,
            createdAt: Date.now(),
        };
        dispatch(createPoll(payload));
        resetForm();
        setOpen(false);
    };

    const handleDelete = (id) => {
        dispatch(deletePoll(id));
    };

    const currentSections = useMemo(() => {
        if (!classId) return [];
        const cls = classesState.find(c => c.id === classId);
        return cls?.sections || [];
    }, [classesState, classId]);

    const targetSummary = (poll) => {
        switch (poll.targetCategory) {
            case 'class': return 'Class';
            case 'section': return 'Class-Section';
            case 'teachers': return 'Teachers';
            case 'non-teaching': return 'Non-Teaching Staff';
            default: return 'All';
        }
    };

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight={600} display="flex" alignItems="center" gap={1}>
                    <HowToVoteIcon color="primary"/> Polls
                </Typography>
                <Button variant="contained" startIcon={<AddIcon/>} onClick={() => {resetForm(); setOpen(true);}}>
                    New Poll
                </Button>
            </Stack>

            <Grid container spacing={2}>
                {polls.map(poll => (
                    <Grid item xs={12} md={6} lg={4} key={poll.id}>
                        <Card variant="outlined" sx={{height:'100%', display:'flex', flexDirection:'column'}}>
                            <CardContent sx={{flex:1}}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                    <Typography variant="subtitle1" fontWeight={600}>{poll.question}</Typography>
                                    <Tooltip title="Delete Poll">
                                        <IconButton size="small" onClick={() => handleDelete(poll.id)}>
                                            <DeleteIcon fontSize="small" color="error"/>
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                <Chip label={targetSummary(poll)} size="small" sx={{mb:1}}/>
                                <Divider sx={{mb:1}}/>
                                <Stack spacing={0.5}>
                                    {poll.options.map(o => (
                                        <Box key={o.id} sx={{display:'flex', flexDirection:'column', width:'100%', mb:1}}>
                                            <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'center', mb:0.5}}>
                                                <Typography variant="body2">{o.text}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {o.votes || 0} {(o.votes === 1) ? 'vote' : 'votes'}
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={getTotalVotes(poll) > 0 ? (o.votes || 0) / getTotalVotes(poll) * 100 : 0}
                                                sx={{height:6, borderRadius:1}}
                                            />
                                            {getUserVote(poll.id) === o.id && (
                                                <Box sx={{display:'flex', alignItems:'center', mt:0.5}}>
                                                    <CheckCircleIcon color="success" fontSize="small" sx={{mr:0.5}} />
                                                    <Typography variant="caption" color="success.main">Your vote</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                                <Typography variant="caption" color="text.secondary" sx={{display:'block', mt:1}}>
                                    Created {new Date(poll.createdAt).toLocaleString()}
                                </Typography>
                            </CardContent>
                            <CardActions sx={{justifyContent:'flex-end', pt:0}}>
                                {!hasUserVoted(poll.id) && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<HowToVoteIcon />}
                                        onClick={() => handleOpenVoteDialog(poll.id)}
                                    >
                                        Vote
                                    </Button>
                                )}
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
                {!loading && polls.length === 0 && (
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">No polls created yet.</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>

            {/* Create Poll Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
                <DialogTitle sx={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                    Create Poll
                    <IconButton onClick={() => setOpen(false)} size="small"><CloseIcon/></IconButton>
                </DialogTitle>
                <Divider/>
                <DialogContent sx={{pt:3}}>
                    <Stack spacing={3}>
                        {error && <Typography variant="body2" color="error">{error}</Typography>}
                        <TextField
                            label="Question"
                            value={question}
                            onChange={e => setQuestion(e.target.value)}
                            fullWidth
                            placeholder="Enter poll question"
                        />
                        <Box>
                            <Typography variant="subtitle2" mb={1}>Options</Typography>
                            <Stack spacing={1}>
                                {options.map((opt, idx) => (
                                    <Stack direction="row" spacing={1} key={opt.id} alignItems="center">
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={opt.text}
                                            onChange={(e) => handleOptionChange(opt.id, e.target.value)}
                                            placeholder={`Option ${idx+1}`}
                                        />
                                        <IconButton size="small" disabled={options.length <= 2} onClick={() => handleRemoveOption(opt.id)}>
                                            <DeleteIcon fontSize="small"/>
                                        </IconButton>
                                    </Stack>
                                ))}
                                <Button size="small" variant="text" onClick={handleAddOption}>Add Option</Button>
                            </Stack>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" mb={1}>Target Audience</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Target"
                                        value={targetCategory}
                                        onChange={(e) => { setTargetCategory(e.target.value); setClassId(''); setSectionId(''); }}
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="class">Class</MenuItem>
                                        <MenuItem value="section">Class Section</MenuItem>
                                        <MenuItem value="teachers">Teachers</MenuItem>
                                        <MenuItem value="non-teaching">Non Teaching Staff</MenuItem>
                                    </TextField>
                                </Grid>
                                {(targetCategory === 'class' || targetCategory === 'section') && (
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Class"
                                            value={classId}
                                            onChange={(e) => { setClassId(e.target.value); setSectionId(''); }}
                                        >
                                            {classesState.map(cls => <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>)}
                                        </TextField>
                                    </Grid>
                                )}
                                {targetCategory === 'section' && (
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Section"
                                            value={sectionId}
                                            onChange={(e) => setSectionId(e.target.value)}
                                            disabled={!classId}
                                        >
                                            {currentSections.map(sec => <MenuItem key={sec.id} value={sec.id}>{sec.name}</MenuItem>)}
                                        </TextField>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>

                        {targetCategory === 'teachers' && (
                            <Typography variant="caption" color="text.secondary">Targeting {teachingStaff.length} teaching staff members.</Typography>
                        )}
                        {targetCategory === 'non-teaching' && (
                            <Typography variant="caption" color="text.secondary">Targeting {nonTeachingStaff.length} non-teaching staff members.</Typography>
                        )}

                        <Stack direction="row" justifyContent="flex-end" spacing={2}>
                            <Button onClick={() => setOpen(false)}>Cancel</Button>
                            <Button variant="contained" onClick={handleSubmit}>Create</Button>
                        </Stack>
                    </Stack>
                </DialogContent>
            </Dialog>

            {/* Vote Dialog */}
            <Dialog open={voteDialogOpen} onClose={handleCloseVoteDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Vote on Poll</DialogTitle>
                <Divider/>
                <DialogContent>
                    {currentPoll ? (
                        <Stack spacing={2}>
                            <Typography variant="subtitle1">{currentPoll.question}</Typography>
                            <Stack spacing={1}>
                                {currentPoll.options.map(option => (
                                    <Button
                                        key={option.id}
                                        variant={selectedOption === option.id ? 'contained' : 'outlined'}
                                        color="primary"
                                        onClick={() => setSelectedOption(option.id)}
                                        sx={{justifyContent:'flex-start'}}
                                    >
                                        <Radio
                                            checked={selectedOption === option.id}
                                            onChange={() => setSelectedOption(option.id)}
                                            value={option.id}
                                            name="vote-option"
                                            color="primary"
                                            sx={{mr:1}}
                                        />
                                        {option.text}
                                    </Button>
                                ))}
                            </Stack>
                            <Divider/>
                            <Stack direction="row" justifyContent="flex-end" spacing={2}>
                                <Button onClick={handleCloseVoteDialog} color="inherit">Cancel</Button>
                                <Button variant="contained" onClick={handleVoteSubmit} disabled={!selectedOption}>
                                    Submit Vote
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary">Poll not found.</Typography>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default PollPage;
